import { useEffect, useRef, useState, type KeyboardEvent } from 'react'

/**
 * The zoom percent readout — a static label, or a click-to-edit number input
 * when `onSetPercent` is given (FS-562 type-in). Deliberately dependency-light
 * (no i18n / no other components): the label is a prop, so toolbar surfaces
 * (PDF viewer, Data Catalog preview) can reuse the exact type-in behavior with
 * their own chrome, and importing it never pulls in the i18n runtime.
 */

const READOUT_CLASS =
  'text-[13px] font-semibold text-foreground min-w-[44px] h-9 text-center select-none tabular-nums'
const READOUT_STATIC = READOUT_CLASS + ' leading-9'
const READOUT_BUTTON =
  READOUT_CLASS +
  ' rounded-md hover:bg-black/[0.05] dark:hover:bg-white/10 transition-colors cursor-text'
const READOUT_INPUT =
  READOUT_CLASS +
  ' bg-transparent outline-none rounded-md ring-1 ring-inset ring-[#e8eaee] dark:ring-dark-border focus:ring-2 focus:ring-blue-500'

export interface ZoomReadoutProps {
  /** Current zoom in display percent. */
  percent: number
  /** When provided, the readout becomes click-to-edit type-in. Wire it to
   *  `useZoom.setPercent` (or a setter that snaps + clamps) — it's called with
   *  the raw typed value. */
  onSetPercent?: (percent: number) => void
  /** Accessible label / tooltip for the readout (already translated). */
  label: string
  /** Override the static/clickable readout class (defaults to the pill style).
   *  Toolbar surfaces pass their own chrome here. */
  className?: string
  /** Override the edit-input class. */
  inputClassName?: string
}

export function ZoomReadout({ percent, onSetPercent, label, className, inputClassName }: ZoomReadoutProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  // Escape must cancel. setEditing(false) unmounts the focused <input>, and real
  // browsers fire a native blur on removal that reaches onBlur={commit} — so
  // without this guard Escape would still commit the draft. (jsdom doesn't fire
  // blur-on-unmount, which is why the unit test didn't catch it.) FS-562 review.
  const cancelNextCommitRef = useRef(false)

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  if (!onSetPercent) {
    return <span className={className ?? READOUT_STATIC}>{percent}%</span>
  }

  const commit = () => {
    if (cancelNextCommitRef.current) {
      cancelNextCommitRef.current = false
      setEditing(false)
      return
    }
    const parsed = parseInt(draft, 10)
    if (!Number.isNaN(parsed)) onSetPercent(parsed)
    setEditing(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelNextCommitRef.current = true
      setEditing(false)
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        className={inputClassName ?? READOUT_INPUT}
      />
    )
  }

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => {
        cancelNextCommitRef.current = false
        setDraft(String(percent))
        setEditing(true)
      }}
      className={className ?? READOUT_BUTTON}
    >
      {percent}%
    </button>
  )
}

export default ZoomReadout
